/* ============================================
   Image Strip / Lifestyle
   assets/image-strip.js

   Autoplay carousel — runs only on mobile (<750px).
   On desktop slides are a flex grid, no JS needed.
   ============================================ */

(function () {
  'use strict';

  const MOBILE_QUERY = '(max-width: 749px)';

  class ImageStrip {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-strip-viewport]');
      this.track = root.querySelector('[data-strip-track]');
      this.autoplay = root.dataset.autoplay === 'true';
      this.speed = parseFloat(root.dataset.speed) || 50;
      this.pauseDelay = parseInt(root.dataset.pauseDelay, 10) || 2000;

      this.isVisible = false;
      this.isPlaying = false;
      this.isPaused = false;
      this.rafId = null;
      this.lastTime = null;
      this.pauseTimer = null;
      this.halfWidth = 0;

      if (!this.viewport || !this.track) return;
      if (!this.autoplay) return;
      if (!this.isMobile()) return;

      this.init();
    }

    isMobile() {
      return window.matchMedia(MOBILE_QUERY).matches;
    }

    init() {
      this.duplicateSlides();
      this.observe();
      this.bindInteraction();
    }

    duplicateSlides() {
      const slides = Array.from(this.track.children);
      if (!slides.length) return;

      slides.forEach((slide) => {
        const clone = slide.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        this.track.appendChild(clone);
      });

      const measure = () => {
        this.halfWidth = this.track.scrollWidth / 2;
      };

      requestAnimationFrame(measure);

      const images = this.track.querySelectorAll('img');
      let loaded = 0;

      images.forEach((img) => {
        if (img.complete) {
          loaded += 1;
          if (loaded === images.length) requestAnimationFrame(measure);
        } else {
          img.addEventListener('load', () => {
            loaded += 1;
            if (loaded === images.length) requestAnimationFrame(measure);
          });
        }
      });

      window.addEventListener('resize', () => {
        requestAnimationFrame(measure);
      });
    }

    observe() {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.isVisible = entry.isIntersecting;

            if (this.isVisible) {
              this.play();
            } else {
              this.pause();
            }
          });
        },
        { threshold: 0.1 }
      );

      observer.observe(this.root);
    }

    bindInteraction() {
      const onInteract = () => {
        this.isPaused = true;
        this.pause();

        clearTimeout(this.pauseTimer);
        this.pauseTimer = setTimeout(() => {
          this.isPaused = false;
          if (this.isVisible) this.play();
        }, this.pauseDelay);
      };

      this.viewport.addEventListener('touchstart', onInteract, { passive: true });
      this.viewport.addEventListener('mousedown', onInteract);
      this.viewport.addEventListener('wheel', onInteract, { passive: true });
    }

    play() {
      if (this.isPlaying || this.isPaused || !this.isVisible) return;

      this.isPlaying = true;
      this.lastTime = null;
      this.tick();
    }

    pause() {
      this.isPlaying = false;

      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    tick() {
      this.rafId = requestAnimationFrame((time) => {
        if (!this.isPlaying) return;

        if (this.lastTime !== null) {
          const delta = time - this.lastTime;
          const move = (this.speed * delta) / 1000;

          this.viewport.scrollLeft += move;

          if (this.halfWidth && this.viewport.scrollLeft >= this.halfWidth) {
            this.viewport.scrollLeft -= this.halfWidth;
          }
        }

        this.lastTime = time;
        this.tick();
      });
    }
  }

  function init(container) {
    const scope = container || document;

    scope.querySelectorAll('[data-image-strip]').forEach((root) => {
      if (root.__imageStripInit) return;

      root.__imageStripInit = true;
      new ImageStrip(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document));
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => init(event.target));
})();
